import { OrderOffer, OrderOfferSammendrag, OrderOfferStatus } from "codegen";
import { format, formatDate } from "date-fns";
import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import THEME from "theme";

import { faEnvelope, faEnvelopeOpen, faFilterList, faInfoCircle, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Direction, Flex, Justify, Wrap } from "@ntemarked/mittne-components";
import StyledText from "@ntemarked/mittne-components/dist/StyledText/StyledText";

import TextCopyButton from "components/molecules/TextCopyButton";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { Table } from "components/organisms/Table";
import FormButton from "components/organisms/form/FormButton";
import { ToggleFieldClean } from "components/organisms/form/ToggleField";
import { useQuery } from "hooks/useParamsState";
import useQueryParamsState from "hooks/useQueryParamsState";
import { useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { OfferFilter, OfferFilterTable } from "pages/Strombestilling/orderoffers/OrderOfferFilterTable";
import { ResendOrderOfferButton } from "pages/Strombestilling/orderoffers/ResendOrderOfferButton";
import { DatePicker } from "pages/Strombestilling/orders/components/DatePicker";
import { DeleteOrderOfferButton } from "pages/Strombestilling/orders/components/DeleteOrderOfferButton";
import { Tooltip, TooltipText } from "pages/Strombestilling/orders/components/OrderStatus";
import { FilterWrapper } from "pages/Strombestilling/orders/components/OrdersFilters";
import { getStromtilbudUrlRoot } from "utils/Utils";

export const newOfferString = "new";

export const OfferStatusMap: Map<OrderOfferStatus | undefined, string> = new Map([
	[OrderOfferStatus.Created, "Tilbud opprettet"],
	[OrderOfferStatus.Opened, "Åpnet av kunde"],
	[OrderOfferStatus.Ordered, "Bestilt av kunde"],
	[OrderOfferStatus.CreditCheckBlocked, "Kredittsjekk blokkert"],
	[OrderOfferStatus.CreditCheckFailed, "Kredittsjekk feilet"],
	[OrderOfferStatus.Expired, "Utgått"],
	[OrderOfferStatus.PendingConsent, "Venter på samtykke"],
	[OrderOfferStatus.ConsentExpired, "Samtykke utløpt"],
	[OrderOfferStatus.ConsentRejected, "Samtykke avvist"],
	[OrderOfferStatus.ConsentError, "Feil ved samtykke"],
	[undefined, "Ukjent status"],
]);

// Lukkede tilbud kan ikke sendes på nytt. Speiler closedStatuses i strombestilling-api, minus ORDERED.
const closedOfferStatuses: OrderOfferStatus[] = [
	OrderOfferStatus.Expired,
	OrderOfferStatus.ConsentExpired,
	OrderOfferStatus.ConsentRejected,
	OrderOfferStatus.ConsentError,
];

const isClosed = (status?: OrderOfferStatus) => !!status && closedOfferStatuses.includes(status);

export const ChannelMap: Map<string, string> = new Map([
	["WEB/kundesentertelefon", "Telefon"],
	["WEB/kundesenterepost", "Epost"],
	["WEB/kundesenterchat", "Chat"],
]);

export default function OffersPage() {
	const today = new Date();
	const [selectedDate, setSelectedDate] = useState<Date>(today); // Kun for datepicker, ikke queryparam
	const [month, setMonth] = useQueryParamsState("month");
	const [isFullYear, setIsFullYear] = useQueryParamsState("year");
	const [status, setStatus] = useQueryParamsState("status");
	const [channel, setChannel] = useQueryParamsState("kanal");
	const [createdBy, setCreatedBy] = useQueryParamsState("createdBy");
	const [product, setProduct] = useQueryParamsState("product");

	const [customerName, setCustomerName] = useQueryParamsState("name");
	// For å kunne resette søkestrenger når man fjerner filter må søkefeltet ha en egen state, i tillegg til queryparam staten
	// Man kan ikke bruke samme ettersom debouncen legger inn et delay i oppdateringen
	const [searchName, setSearchName] = useState(customerName ?? "");
	const [showFilters, setShowFilters] = useState(false);
	const [hovered, setHovered] = useState<string>("");

	function resetFilters() {
		setSelectedDate(today);
		setMonth("");
		setIsFullYear("");
		setStatus("");
		setCustomerName("");
		setSearchName("");
		setProduct("");
		setChannel("");
		setCreatedBy("");
		setShowFilters(false);
	}

	const anyFilterActivated = Boolean(month ?? isFullYear ?? status ?? customerName ?? product ?? channel ?? createdBy);

	const params = useQuery();

	const { data, isLoading, error } = useStrombestillingApiQuery<OrderOfferSammendrag>(`/orderoffer/tilbudoversikt?${params.toString()}`, ["orderoffers"]);
	const debouncedSetCustomerName = useMemo(() => debounce((newSearch: string) => setCustomerName(newSearch), 200), [setCustomerName]);

	if (error) {
		return <p>Feil ved lasting av strømtilbud: {error.message}</p>;
	}

	return (
		<>
			<MainLayoutPageHeader
				title={
					<Flex gap="var(--grid-2)">
						Tilbud til oppfølging
						<Link to={newOfferString} title="Nytt strømtilbud">
							<FontAwesomeIcon icon={faPlusCircle} />
						</Link>
					</Flex>
				}
				intro="Tilbud utgår ved midnatt dagen etter opprettelse. Etter to uker vil de automatisk anonymiseres."
			/>
			<Flex direction={Direction.row} justifyContent={Justify.spaceBetween}>
				<Flex direction={Direction.row} gap="20px" alignItems={Justify.center}>
					<DatePicker
						date={selectedDate}
						setDate={(newDate) => {
							setSelectedDate(newDate);
							setMonth(format(newDate, "yyyy-MM"));
						}}
						isFullYear={!!isFullYear}
					/>
					<ToggleFieldClean
						checked={!!isFullYear}
						onChange={() => {
							if (isFullYear) {
								setIsFullYear("");
								setShowFilters(false);
							} else {
								setIsFullYear("true");
								setShowFilters(true);
							}
						}}
						label={`Vis statistikk for ${format(selectedDate, "yyyy")}`}
						wrapperStyle={{ width: "auto" }}
						labelStyle={{ color: "#28292c", fontWeight: 500 }}
					/>
				</Flex>
				<Flex direction={Direction.row} gap="20px" alignItems={Justify.center}>
					{anyFilterActivated && (
						<Link to="" onClick={resetFilters}>
							Nullstill filter
						</Link>
					)}
					<FormButton
						style={{ width: "180px" }}
						label={`${showFilters ? "Skjul" : "Åpne"} filtre`}
						icon={<FontAwesomeIcon style={{ marginLeft: "0.5rem" }} icon={faFilterList} />}
						onClick={() => setShowFilters(!showFilters)}
					/>
				</Flex>
			</Flex>
			{showFilters && data && (
				<FilterWrapper>
					<Flex direction={Direction.row} gap="2rem" alignItems={Justify.start} wrap={Wrap.wrap}>
						{data?.perStatus && Boolean(Object.entries(data?.perStatus).length) && (
							<OfferFilterTable name="Status" data={data?.perStatus} selected={status} setSelected={setStatus} />
						)}
						{data?.perProdukt && Boolean(Object.entries(data.perProdukt).length) && (
							<OfferFilterTable name="Produkt" data={data.perProdukt} selected={product} setSelected={setProduct} />
						)}
						{data?.perSalgskanal && Boolean(Object.entries(data.perSalgskanal).length) && (
							<OfferFilterTable name="Kanal" data={data.perSalgskanal} selected={channel} setSelected={setChannel} />
						)}
						{data?.perCreatedBy && Boolean(Object.entries(data?.perCreatedBy).length) && (
							<OfferFilterTable
								name="Opprettet av"
								data={data.perCreatedBy}
								selected={createdBy}
								setSelected={setCreatedBy}
								toSentenceCase={false}
							/>
						)}
					</Flex>
				</FilterWrapper>
			)}
			<Flex direction={Direction.row} gap="20px" alignItems={Justify.center} justifyContent={Justify.spaceBetween}>
				{data && (
					<p>
						Viser {data.filtrertAntall} av {data.totaltAntall} tilbud
					</p>
				)}
				<input
					style={{ width: "800px" }}
					type="text"
					value={searchName}
					onChange={(e) => {
						setSearchName(e.target.value || "");
						debouncedSetCustomerName(e.target.value || "");
					}}
					placeholder="Søk etter kundenavn"
				/>
			</Flex>
			{data?.tilbud && (
				<Table
					loading={isLoading}
					entries={data?.tilbud}
					labels={{
						createdAt: "Opprettet",
						startDate: "Oppstart",
						status: "Status",
						customer: "Kunde",
						tariffId: "TariffID",
						createdBy: "Opprettet av",
						channel: "Kanal",
						delete: "",
					}}
					generators={{
						createdAt: (d: OrderOffer) => formatDate(d.createdAt!, "dd.MM.yyyy, HH:mm"),
						startDate: (d: OrderOffer) => (d.startDate ? formatDate(d.startDate, "dd.MM.yyyy") : "-"),
						status: (d: OrderOffer) => (
							<Flex gap="0.5rem" style={{ width: "220px" }} justifyContent={Justify.spaceBetween} alignItems={Justify.center}>
								<OfferFilter as="div" $status={d.status}>
									{OfferStatusMap.get(d.status) ?? "Ukjent status"}
								</OfferFilter>
								{isClosed(d.status) && <OpenedIcon opened={d.hasBeenOpened || false} />}
								{!isClosed(d.status) && (
									<Flex gap="0.5rem" alignItems={Justify.center}>
										{d.status !== OrderOfferStatus.Ordered && !d.anonymized && (
											<ResendOrderOfferButton offerId={d.id!} sendSms={d.sendSMS ?? false} />
										)}
										<TextCopyButton
											color={THEME.colors.primary}
											text={getStromtilbudUrlRoot() + "/" + d.id}
											title="Kopier URL"
											fontSize={"20px"}
										/>
									</Flex>
								)}
							</Flex>
						),
						customer: (d: OrderOffer) => {
							if (d.anonymized) return <StyledText>Anonymisert</StyledText>;
							else {
								const isCompany = d.customer?.organisasjonsnummer!!;
								const customerName = isCompany ? d.customer?.etternavn : `${d.customer?.fornavn} ${d.customer?.etternavn}`;
								return (
									<Tooltip>
										<Flex direction={Direction.row} alignItems={Justify.center} gap="1rem">
											<StyledText>{`${d.customer?.mobil}, ${customerName}`}</StyledText>
											<FontAwesomeIcon icon={faInfoCircle} onMouseEnter={() => setHovered(d.id!)} onMouseLeave={() => setHovered("")} />
										</Flex>
										<TooltipText visible={hovered === d.id}>
											<div>Målepunkt-ID: {d.meteringPointId}</div>
											<div>Målernummer: {d.meterNumber}</div>
										</TooltipText>
									</Tooltip>
								);
							}
						},
						channel: (d: OrderOffer) => ChannelMap.get(d.source!),
						delete: (d: OrderOffer) => <DeleteOrderOfferButton offerId={d.id!} />,
					}}
				/>
			)}
		</>
	);
}

function OpenedIcon(props: { opened: boolean }) {
	return (
		<span title={props.opened ? "Åpnet av kunde" : "Ikke åpnet av kunde"}>
			<FontAwesomeIcon style={{ fontSize: "20px" }} icon={props.opened ? faEnvelopeOpen : faEnvelope} />
		</span>
	);
}
